/*
 *  -----------------------------------------------------------------------
 *    <copyright file="admin-panels.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client/react';
import { orderBy } from '@progress/kendo-data-query';
import { restructuredUpdatedCreatedBy } from '@utils-clean/update-users-array';
import { useAuthorization, useBoolean, useGridCells, useTranslation } from 'hooks';
import { GET_PANELS } from 'queries/central-monitoring';
import { PanelFields } from 'queries/generated/fragments.generated';
import { PanelType } from 'types/graphql-global-types';
import Constants from 'utils/constants';
import AddPanelDialog from 'views/components/central-monitoring/panels/add-panel';
import PanelService from 'views/components/central-monitoring/panels/add-panel/service';
import Button from 'views/components/core/buttons/button';
import ExpandableGrid from 'views/components/core/expandable/grid';
import { GridColumnMenuActiveFilter } from 'views/components/core/grid-filters';
import Loading from 'views/components/core/loading';
import PageTitle from 'views/components/core/page-title/page-title';
import Error from 'views/components/error/error';

import './styles.scss';

const AdminPanels = ({ studyId }: any) => {
    const user = useAuthorization();
    const { t } = useTranslation();
    const canEdit = user.canWriteSystemAdminLevel();
    const isSysAdmin = user.isSystemAdmin(Constants.SystemAdminLevel.SysAdmin);

    const { YesNoCell, DateCell } = useGridCells();

    const openAddPanelDialog = useBoolean(false);
    const [currentPanel, setCurrentPanel] = useState<PanelFields | null>(null);
    const [panels, setPanels] = useState([]);
    const { data, error, loading } = useQuery(GET_PANELS, {
        variables: {
            organisationId: user.organisationId,
            studyId: studyId ?? Constants.ObjectId.Empty,
        },
    });

    useEffect(() => {
        if (data && data.panels) {
            const excludedPanels: (PanelType | null)[] = [
                PanelType.TRI_OP_DATE,
                PanelType.EVENT_RATES_OVER_TIME,
                PanelType.PROPORTIONS_OVER_TIME,
                PanelType.TIMELINE,
            ];

            let items = data.panels
                .filter((p: PanelFields) => !excludedPanels.includes(p.panelType))
                .map((panel: PanelFields) => {
                    const model = PanelService.getPanelModel(panel.modelParams);
                    return {
                        ...panel,
                        panelType: panel.panelType === PanelType.MODEL ? t('cm:Model') : t(`cm:${panel.graph.type}`),
                        model: panel.panelType === PanelType.MODEL ? t(`cm:${model}`) : '',
                    };
                });

            const newData = restructuredUpdatedCreatedBy(items);
            items = orderBy(newData, [
                { field: 'active', dir: 'desc' },
                { field: 'name', dir: 'asc' },
            ]);
            setPanels(items);
        }
    }, [data]);

    if (!studyId) return <h3>{t('common:noStudySelected')}</h3>;

    if (!user || !user.systemAdminLevel) return null;

    if (loading) return <Loading />;

    if (error) return <Error error={error} />;

    const actions = (item: PanelFields) => [
        {
            label: 'cm:editPanel',
            icon: ['fal', 'edit'],
            onClick: () => {
                setCurrentPanel(item);
                openAddPanelDialog.setTrue();
            },
            enabled: canEdit || isSysAdmin,
        },
    ];

    return (
        <>
            <AddPanelDialog
                currentPanel={currentPanel}
                open={openAddPanelDialog.value}
                studyId={studyId}
                onClose={() => {
                    setCurrentPanel(null);
                    openAddPanelDialog.setFalse();
                }}
                panels={panels}
            />
            <PageTitle text={t('cm:panels')} />
            <div className="btn-wrapper-create-panel top-title-btn-wrapper">
                <Button
                    className="panel-add-btn main-button"
                    text="cm:addPanel"
                    disabled={!canEdit}
                    onClick={() => {
                        openAddPanelDialog.setTrue();
                    }}
                />
            </div>
            <ExpandableGrid
                customKey="panels-grid"
                id="panelsGrid"
                key="panelsGrid"
                items={panels}
                rowActions={actions}
                groupable={false}
                showPager
                scrollable="scrollable"
                reorderable
                addScroll
                pageable={false}
                resizable
                options={{
                    allowExcelExport: false,
                    allowExpandCollapseAll: false,
                    useFilterChips: false,
                }}
                detail={null}
                columns={[
                    {
                        field: 'active',
                        label: t('common:active'),
                        id: 'panel-active',
                        minWidth: 140,
                        cells: { data: YesNoCell },
                        filterUi: GridColumnMenuActiveFilter,
                        lockedByDefault: true,
                    },
                    {
                        field: 'name',
                        label: t('cm:panelName'),
                        id: 'panel-panelName',
                        minWidth: 180,
                        lockedByDefault: true,
                    },
                    {
                        field: 'panelType',
                        label: t('cm:panelType'),
                        id: 'panel-panelType',
                        minWidth: 180,
                    },
                    {
                        field: 'model',
                        label: t('cm:model'),
                        id: 'panel-model',
                        minWidth: 140,
                    },
                    {
                        field: 'graph.plotTitle',
                        label: t('cm:plotTitle'),
                        id: 'panel-plotTitle',
                        minWidth: 180,
                    },
                    {
                        field: 'graph.xTitle',
                        label: t('cm:xTitle'),
                        id: 'panel-xTitle',
                        minWidth: 180,
                    },
                    {
                        field: 'graph.yTitle',
                        label: t('cm:yTitle'),
                        id: 'panel-yTitle',
                        minWidth: 180,
                    },
                    {
                        field: 'weight',
                        label: t('cm:weight'),
                        id: 'panel-weight',
                        minWidth: 140,
                        filter: 'numeric',
                    },
                    {
                        field: 'decision',
                        label: t('cm:decision'),
                        id: 'panel-decision',
                        cells: { data: YesNoCell },
                        filterUi: GridColumnMenuActiveFilter,
                        minWidth: 140,
                    },
                    {
                        field: 'includeInSiteScoring',
                        label: t('cm:includeInScoring'),
                        id: 'panel-includeInSiteScoring',
                        minWidth: 210,
                        cells: { data: YesNoCell },
                        filterUi: GridColumnMenuActiveFilter,
                    },
                    {
                        label: t('ra:createdBy'),
                        field: '_createdByString',
                        id: 'panel-createdby',
                        minWidth: 180,
                    },
                    {
                        label: t('ra:createdDate'),
                        field: 'created',
                        id: 'panel-created',
                        filter: 'date',
                        minWidth: 180,
                        cells: { data: DateCell },
                    },
                    {
                        label: t('ra:updatedBy'),
                        field: '_updatedByString',
                        id: 'panel-updatedby',
                        minWidth: 180,
                    },
                    {
                        label: t('ra:updatedDate'),
                        field: 'updated',
                        id: 'panel-updated',
                        filter: 'date',
                        minWidth: 190,
                        cells: { data: DateCell },
                    },
                    {
                        field: 'recordId',
                        label: 'ID',
                        minWidth: 120,
                        id: 'panel-id',
                        filter: 'numeric',
                        showByDefault: false,
                    },
                ]}
            />
        </>
    );
};

export default AdminPanels;
